/**
 * Die Verknuepfungsregel: Bereich -> Ziel -> Projekt -> Aufgabe (Roadmap F-11).
 *
 * Rein und ohne Dexie. Dieselbe Funktion bringt drei Wege in Ordnung, damit
 * die Regel nicht dreimal verschieden gebaut wird:
 *   - das Datenbank-Upgrade (bestehende Daten im Browser),
 *   - die Migration alter Sicherungsdateien,
 *   - die Tests.
 * Die Schreibwege in `db/repositories/links.ts` halten dieselbe Regel beim
 * einzelnen Aendern ein.
 *
 * Die Regel:
 *   1. Hat ein Projekt ein Ziel, ist sein Bereich der des Ziels. Sonst kann es
 *      nicht passieren, dass ein Projekt unter "Sport" haengt, sein Ziel aber
 *      unter "Finanzen".
 *   2. Eine Aufgabe haengt an einem Projekt ODER direkt an einem Ziel, nie an
 *      beidem - sonst gaebe es zwei Wege nach oben, die sich widersprechen
 *      koennten. Ihr Bereich folgt dem naechsten Elternteil.
 *   3. Ohne Elternteil wird der Bereich frei gewaehlt.
 *   4. Verweise ins Leere werden entfernt. Geloescht wird dabei NIE ein
 *      Eintrag, nur ein Verweis.
 *
 * Nur die Felder, die die Regel braucht, sind gefordert - so passen sowohl
 * Datenbankzeilen als auch lose Zeilen aus einer Sicherungsdatei.
 */
import type { ID } from '@/domain/types';

export interface GoalLinks { id: ID; areaId: ID | null; parentGoalId?: ID }
export interface ProjectLinks { id: ID; areaId: ID | null; goalId?: ID }
export interface TaskLinks { id: ID; areaId: ID | null; projectId?: ID; goalId?: ID }

export interface LinkInput<G extends GoalLinks, P extends ProjectLinks, T extends TaskLinks> {
  /** Alle vorhandenen Bereichs-IDs, archivierte eingeschlossen. */
  areaIds: Iterable<ID>;
  goals: G[];
  projects: P[];
  tasks: T[];
}

export interface LinkResult<G, P, T> {
  goals: G[];
  projects: P[];
  tasks: T[];
  /** IDs der Zeilen, die sich geaendert haben - nur die muessen geschrieben werden. */
  changed: { goals: Set<ID>; projects: Set<ID>; tasks: Set<ID> };
}

/** Ein Objekt ohne einen Schluessel - `undefined` wuerde als Feld gespeichert. */
function ohne<T extends object, K extends keyof T>(row: T, key: K): T {
  const kopie = { ...row };
  delete kopie[key];
  return kopie;
}

export function reconcileLinks<G extends GoalLinks, P extends ProjectLinks, T extends TaskLinks>(
  input: LinkInput<G, P, T>,
): LinkResult<G, P, T> {
  const areaIds = new Set(input.areaIds);
  const changed = { goals: new Set<ID>(), projects: new Set<ID>(), tasks: new Set<ID>() };
  const gueltigerBereich = (areaId: ID | null) => (areaId !== null && areaIds.has(areaId) ? areaId : null);

  // --- Ziele: nur verwaiste Verweise; ihr Bereich wird frei gewaehlt. -------
  const goalIds = new Set(input.goals.map((g) => g.id));
  const goals = input.goals.map((g) => {
    let neu = g;
    const bereich = gueltigerBereich(g.areaId);
    if (bereich !== g.areaId) neu = { ...neu, areaId: bereich };
    if (g.parentGoalId !== undefined && !goalIds.has(g.parentGoalId)) neu = ohne(neu, 'parentGoalId');
    if (neu !== g) changed.goals.add(g.id);
    return neu;
  });
  const goalById = new Map(goals.map((g) => [g.id, g]));

  // --- Projekte: Bereich folgt dem Ziel. -------------------------------------
  const projects = input.projects.map((p) => {
    let neu = p;
    if (p.goalId !== undefined && !goalById.has(p.goalId)) neu = ohne(neu, 'goalId');

    const ziel = neu.goalId !== undefined ? goalById.get(neu.goalId) : undefined;
    const bereich = ziel ? ziel.areaId : gueltigerBereich(neu.areaId);
    if (bereich !== neu.areaId) neu = { ...neu, areaId: bereich };

    if (neu !== p) changed.projects.add(p.id);
    return neu;
  });
  const projectById = new Map(projects.map((p) => [p.id, p]));

  // --- Aufgaben: Projekt ODER Ziel, Bereich vom naechsten Elternteil. --------
  const tasks = input.tasks.map((t) => {
    let neu = t;
    if (t.projectId !== undefined && !projectById.has(t.projectId)) neu = ohne(neu, 'projectId');
    if (neu.goalId !== undefined && !goalById.has(neu.goalId)) neu = ohne(neu, 'goalId');
    // Mit Projekt fuehrt der Weg nach oben ueber das Projekt - ein zweiter,
    // direkter Zielverweis koennte ihm widersprechen.
    if (neu.projectId !== undefined && neu.goalId !== undefined) neu = ohne(neu, 'goalId');

    const eltern = neu.projectId !== undefined
      ? projectById.get(neu.projectId)
      : neu.goalId !== undefined ? goalById.get(neu.goalId) : undefined;
    const bereich = eltern ? eltern.areaId : gueltigerBereich(neu.areaId);
    if (bereich !== neu.areaId) neu = { ...neu, areaId: bereich };

    if (neu !== t) changed.tasks.add(t.id);
    return neu;
  });

  return { goals, projects, tasks, changed };
}

/**
 * Bereichsverweise ins Leere in einer beliebigen Tabelle entfernen.
 *
 * Fuer Tabellen ausserhalb der Hierarchie (Ressourcen, Buecher, ...), die
 * frueher beim Loeschen eines Bereichs vergessen wurden.
 */
export function clearDanglingAreas<R extends { id: ID; areaId: ID | null }>(
  rows: R[],
  areaIds: Iterable<ID>,
): { rows: R[]; changed: Set<ID> } {
  const vorhanden = new Set(areaIds);
  const changed = new Set<ID>();
  const neu = rows.map((r) => {
    if (r.areaId === null || vorhanden.has(r.areaId)) return r;
    changed.add(r.id);
    return { ...r, areaId: null };
  });
  return { rows: neu, changed };
}
